import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Combobox, type ComboboxOption } from "@/shared/ui/Combobox";
import { useDebounce } from "@/shared/lib/useDebounce";
import { useCustomerSearch } from "../hooks";
import type { CustomerListItem } from "@/features/customers/types";

// Searchable existing-customer picker — same look as ProductSelect / ColorSelect (shared <Combobox>),
// but the result list is server-driven: the typed query is debounced and hits the customer-search
// endpoint, and cmdk's client-side filtering is disabled so the API results show verbatim.
export function CustomerSelect({
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  invalid,
  id,
}: {
  value: CustomerListItem | null;
  onChange: (customer: CustomerListItem | null) => void;
  placeholder: string;
  searchPlaceholder: string;
  invalid?: boolean;
  id?: string;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const debounced = useDebounce(query, 350);
  const { data, isFetching } = useCustomerSearch(debounced, query.trim().length > 0);
  const results = data?.results ?? [];

  const options: ComboboxOption[] = results.map((c) => ({
    id: String(c.id),
    label: `${c.first_name} ${c.last_name}`.trim(),
    sublabel: c.phone_number,
  }));

  // "Type to search" before anything is entered; "no results" once a query came back empty.
  const emptyText =
    query.trim().length === 0
      ? t("import.agreementAdd.customer.searchHint")
      : t("import.agreementAdd.customer.empty");

  return (
    <Combobox
      id={id}
      options={options}
      value={value ? String(value.id) : ""}
      onSelect={(picked) => {
        const customer = results.find((c) => String(c.id) === picked);
        if (customer) onChange(customer);
        setQuery("");
      }}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      invalid={invalid}
      // Keep the chosen customer's name on the trigger even though it's not in the (cleared) result set.
      triggerLabel={value ? `${value.first_name} ${value.last_name}`.trim() : undefined}
      searchValue={query}
      onSearchChange={setQuery}
      shouldFilter={false}
      loadingList={isFetching}
      emptyText={emptyText}
      onOpenChange={(open) => {
        if (!open) setQuery("");
      }}
    />
  );
}
